import React, { useEffect } from 'react';
import { FileText, CheckCircle, AlertCircle } from 'lucide-react';

const TermsConditionsPage = () => {
  useEffect(() => {
    document.title = 'Terms & Conditions | Indian Cyber Club Academy';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-black min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">Legal Agreement</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4">
            Terms & <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Conditions</span>
          </h1>
          <p className="text-gray-400">Last Updated: January 30, 2025</p>
        </div>

        {/* Intro */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-xl p-8 mb-8">
          <p className="text-gray-300 leading-relaxed">
            Welcome to <strong className="text-white">Indian Cyber Club Academy</strong>. These Terms and Conditions govern your use of our website and services. By accessing our website or enrolling in our courses, you agree to be bound by these terms.
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-red-500" />
              Acceptance of Terms
            </h2>
            <p className="text-gray-300 leading-relaxed">
              By using our website, enrolling in courses, or accessing our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, along with our Privacy Policy and Refund Policy.
            </p>
          </div>

          {/* Section 2 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Course Enrollment</h2>
            <ul className="space-y-3">
              {[
                'You must be at least 16 years old to enroll in our courses',
                'You must provide accurate and complete information during registration',
                'Course fees must be paid in full before accessing course materials',
                'Enrollment is non-transferable to another person',
                'Course access is valid for the duration specified at the time of enrollment'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 3 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Payment Terms</h2>
            <ul className="space-y-3">
              {[
                'All prices are listed in Indian Rupees (INR)',
                'Payment must be made through our authorized payment channels',
                'Course fees are non-refundable as per our Refund Policy',
                'EMI options are subject to approval by financial partners',
                'We reserve the right to change course prices at any time',
                'Any outstanding payments may result in suspension of course access'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 4 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">User Responsibilities</h2>
            <ul className="space-y-3">
              {[
                'Maintain confidentiality of your account credentials',
                'Do not share course materials with unauthorized individuals',
                'Use course content only for personal learning purposes',
                'Comply with all applicable laws while using our services',
                'Report any security breaches or unauthorized access immediately',
                'Attend classes regularly and complete assignments on time'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 5 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Intellectual Property</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              All course materials, including but not limited to videos, documents, code, presentations, and assessments, are the intellectual property of Indian Cyber Club Academy.
            </p>
            <ul className="space-y-3">
              {[
                'You may not reproduce, distribute, or sell course materials',
                'You may not create derivative works from our content',
                'Unauthorized sharing may result in legal action',
                'Screenshots and recordings of live sessions are prohibited without permission'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 6 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Prohibited Activities</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              The following activities are strictly prohibited:
            </p>
            <ul className="space-y-3">
              {[
                'Using skills learned for illegal hacking or unauthorized access',
                'Attacking or compromising systems without proper authorization',
                'Sharing login credentials with others',
                'Uploading malware, viruses, or malicious code',
                'Harassing or threatening instructors or fellow students',
                'Engaging in fraudulent activities or impersonation'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 bg-red-600/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-sm text-red-400">
                <strong>Warning:</strong> Violation of these prohibitions may result in immediate termination of course access, legal action, and reporting to law enforcement authorities.
              </p>
            </div>
          </div>

          {/* Section 7 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Certificates and Credentials</h2>
            <ul className="space-y-3">
              {[
                'Certificates are issued upon successful course completion',
                'Certificate requirements include attendance and assessment completion',
                'Certificates can be verified through our website',
                'Fraudulent use of certificates is a punishable offense',
                'We reserve the right to revoke certificates for misconduct'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 8 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Indian Cyber Club Academy provides courses "as is" without warranties of any kind:
            </p>
            <ul className="space-y-3">
              {[
                'We do not guarantee specific job placements or salary outcomes',
                'We are not liable for technical issues on your end',
                'We are not responsible for third-party service interruptions',
                'Course schedules and content may change without prior notice',
                'We are not liable for misuse of knowledge gained from courses'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 9 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Termination</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We reserve the right to terminate or suspend your access to our services:
            </p>
            <ul className="space-y-3">
              {[
                'For violation of these Terms and Conditions',
                'For non-payment of fees',
                'For prohibited activities or misconduct',
                'For any reason deemed necessary to protect our interests',
                'Without refund of paid fees'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 10 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Governing Law</h2>
            <p className="text-gray-300 leading-relaxed">
              These Terms and Conditions are governed by the laws of India. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of courts in Jaipur, Rajasthan, India.
            </p>
          </div>

          {/* Section 11 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Changes to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on this page. Your continued use of our services after changes constitutes acceptance of the modified terms.
            </p>
          </div>

          {/* Contact */}
          <div className="bg-gradient-to-br from-red-600/10 to-orange-600/10 border-2 border-red-500/30 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-red-500" />
              Contact Information
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              For questions about these Terms and Conditions, please contact us:
            </p>
            <div className="bg-gray-950/50 border border-red-600/30 rounded-lg p-4 space-y-2">
              <p className="text-white">
                <strong>Indian Cyber Club Academy</strong>
              </p>
              <p className="text-gray-300">
                Phone: <a href="tel:+919694612895" className="text-red-500 hover:underline">+91-9694612895</a>
              </p>
              <p className="text-gray-300">
                WhatsApp: <a href="https://wa.me/919694612895" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:underline">+91-9694612895</a>
              </p>
              <p className="text-gray-300">
                Location: Jaipur, Rajasthan, India
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage;
